import { createIsomorphicFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'

/**
 * Resolve the persisted theme on the SERVER so SSR can render the `dark` class on `<html>` directly,
 * eliminating the light→dark flash on hydration. Mirrors `server-locale`'s pattern (read it from the
 * request, not `window`).
 *
 * React 19 hoists the `app.css` stylesheet to the top of `<head>`, so the inline `manageTheme` script
 * (which adds `.dark`) ends up after the render-blocking stylesheet and runs too late — the browser
 * paints light first, then flips to dark. Emitting the class in the server HTML makes the very first
 * paint correct.
 *
 * Only resolves what the server can know: the explicit `theme` cookie (`dark`/`light`). Returns `null`
 * when there's no cookie ('system' mode) — the server can't read `prefers-color-scheme`, so that case
 * stays handled by the inline `manageTheme` script.
 *
 * The CLIENT variant reads the SAME cookie (NOT a no-op). The root loader re-runs on the client
 * (router.invalidate(), query-driven revalidation, intent preloads), and `<html className>` in
 * RootDocument is bound to this value. If the client returned `null`, every re-run would flip an
 * explicit-`dark` visitor's loader value `'dark' → null`, and React would STRIP the `dark` class it
 * painted at SSR → a light→dark flash on a fully-loaded page. Mirroring the cookie keeps the value
 * stable across SSR and client re-runs, so React never touches the class. In system mode (no cookie)
 * it stays `null` on both sides — React leaves `className` undefined and the inline `manageTheme`
 * script's class survives untouched.
 */
const cookieTheme = (cookie: string): 'dark' | 'light' | null => {
  if (/(?:^|;\s*)theme=dark(?:;|$)/.test(cookie)) return 'dark'
  if (/(?:^|;\s*)theme=light(?:;|$)/.test(cookie)) return 'light'
  return null
}

export const resolveServerTheme = createIsomorphicFn()
  .server((): 'dark' | 'light' | null => cookieTheme(getRequest().headers.get('cookie') ?? ''))
  .client((): 'dark' | 'light' | null => cookieTheme(typeof document === 'undefined' ? '' : document.cookie))
